import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { HelmetProvider, Helmet } from "react-helmet-async";
import { Toaster } from "@/components/ui/sonner";
import PWAInstallPrompt from "@/components/PWAInstallPrompt";
import HomePage from "@/pages/HomePage";
import BookingPage from "@/pages/BookingPage";
import SuccessPage from "@/pages/SuccessPage";
import AdminPage from "@/pages/AdminPage";
import DriverRegisterPage from "@/pages/DriverRegisterPage";
import DriverLoginPage from "@/pages/DriverLoginPage";
import DriverDashboardPage from "@/pages/DriverDashboardPage";
import BlogPage from "@/pages/BlogPage";
import ManAndVanLondonPage from "@/pages/ManAndVanLondonPage";
import CarTransportLondonPage from "@/pages/CarTransportLondonPage";
import CourierServiceLondonPage from "@/pages/CourierServiceLondonPage";
import FurnitureDeliveryLondonPage from "@/pages/FurnitureDeliveryLondonPage";
import CustomerAuthPage from "@/pages/CustomerAuthPage";
import CustomerDashboardPage from "@/pages/CustomerDashboardPage";
import CustomerBookingDetailPage from "@/pages/CustomerBookingDetailPage";
import PrivacyPolicyPage from "@/pages/PrivacyPolicyPage";
import TermsPage from "@/pages/TermsPage";
import CookiePolicyPage from "@/pages/CookiePolicyPage";
import AcceptableUsePage from "@/pages/AcceptableUsePage";
import DataDeletionPage from "@/pages/DataDeletionPage";
import SupportPage from "@/pages/SupportPage";
import SeoSlugRouter from "@/seo/pages/SeoSlugRouter";
import MobileShellProvider, { shouldUseMobileShell } from "@/mobile/MobileShellProvider";
import MobileSplash from "@/mobile/MobileSplash";
import MobileTopBar from "@/mobile/MobileTopBar";
import CustomerBottomNav from "@/mobile/CustomerBottomNav";
import {
  IS_WEB,
  IS_CUSTOMER_APP,
  IS_DRIVER_APP,
  TARGET_APP_NAME,
  ENABLE_ADMIN_ROUTE,
  ENABLE_SEO_PAGES,
} from "@/appConfig";

/**
 * Driver-only routes (mounted in `driver` Capacitor target).
 * Customer app users will never see these.
 */
function DriverAppRoutes() {
  return (
    <Routes>
      <Route path="/driver/register" element={<DriverRegisterPage />} />
      <Route path="/driver/login" element={<DriverLoginPage />} />
      <Route path="/driver/dashboard" element={<DriverDashboardPage />} />
      <Route path="/privacy" element={<PrivacyPolicyPage />} />
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/cookies" element={<CookiePolicyPage />} />
      <Route path="/aup" element={<AcceptableUsePage />} />
      <Route path="/data-deletion" element={<DataDeletionPage />} />
      <Route path="/support" element={<SupportPage />} />
      {/* Default + unknown deep links go to login (auth gate handles redirect) */}
      <Route path="/" element={<Navigate to="/driver/login" replace />} />
      <Route path="*" element={<Navigate to="/driver/login" replace />} />
    </Routes>
  );
}

/**
 * Customer-only routes (mounted in `customer` Capacitor target).
 * Driver app users will never see these.
 */
function CustomerAppRoutes() {
  return (
    <Routes>
      <Route path="/" element={<HomePage />} />
      <Route path="/booking/:quoteId" element={<BookingPage />} />
      <Route path="/success" element={<SuccessPage />} />
      <Route path="/customer/login" element={<CustomerAuthPage mode="login" />} />
      <Route path="/customer/register" element={<CustomerAuthPage mode="register" />} />
      <Route path="/customer/dashboard" element={<CustomerDashboardPage />} />
      <Route path="/customer/booking/:bookingId" element={<CustomerBookingDetailPage />} />
      <Route path="/privacy" element={<PrivacyPolicyPage />} />
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/cookies" element={<CookiePolicyPage />} />
      <Route path="/aup" element={<AcceptableUsePage />} />
      <Route path="/data-deletion" element={<DataDeletionPage />} />
      <Route path="/support" element={<SupportPage />} />
      {/* Catch-all deep link → home (keeps booking flow primary) */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

/**
 * Full website routes (default `web` target).
 * IMPORTANT: This block is bit-for-bit identical to the pre-Phase-2 routing.
 * Production transportanything247.com keeps every route + behaviour unchanged.
 */
function WebAppRoutes() {
  return (
    <Routes>
      <Route path="/" element={<HomePage />} />
      <Route path="/booking/:quoteId" element={<BookingPage />} />
      <Route path="/success" element={<SuccessPage />} />
      {ENABLE_ADMIN_ROUTE && <Route path="/admin" element={<AdminPage />} />}
      <Route path="/driver/register" element={<DriverRegisterPage />} />
      <Route path="/driver/login" element={<DriverLoginPage />} />
      <Route path="/driver/dashboard" element={<DriverDashboardPage />} />
      {ENABLE_SEO_PAGES && (
        <>
          <Route path="/blog" element={<BlogPage />} />
          <Route path="/services/man-and-van-london" element={<ManAndVanLondonPage />} />
          <Route path="/services/car-transport-london" element={<CarTransportLondonPage />} />
          <Route path="/services/courier-service-london" element={<CourierServiceLondonPage />} />
          <Route path="/services/furniture-delivery-london" element={<FurnitureDeliveryLondonPage />} />
        </>
      )}
      <Route path="/customer/login" element={<CustomerAuthPage mode="login" />} />
      <Route path="/customer/register" element={<CustomerAuthPage mode="register" />} />
      <Route path="/customer/dashboard" element={<CustomerDashboardPage />} />
      <Route path="/customer/booking/:bookingId" element={<CustomerBookingDetailPage />} />
      <Route path="/privacy" element={<PrivacyPolicyPage />} />
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/cookies" element={<CookiePolicyPage />} />
      <Route path="/aup" element={<AcceptableUsePage />} />
      <Route path="/data-deletion" element={<DataDeletionPage />} />
      <Route path="/support" element={<SupportPage />} />
      {/* SEO landing pages (root-level service + service-city slugs).
          Mounted LAST so explicit routes above always win the match. */}
      {ENABLE_SEO_PAGES && <Route path="/:slug" element={<SeoSlugRouter />} />}
    </Routes>
  );
}

function App() {
  useEffect(() => {
    if (!document.querySelector('[data-rh]')) {
      document.title = TARGET_APP_NAME;
    }
  }, []);

  return (
    <HelmetProvider>
      <Helmet>
        <title>{TARGET_APP_NAME}</title>
        <meta name="title" content={TARGET_APP_NAME} />
        {IS_WEB && (
          <>
            <meta name="description" content="Transport Anything, Anywhere in the UK. From parcels to machinery, get instant transport quotes, live tracking and dedicated delivery in seconds." />
            <link rel="canonical" href="https://transportanything247.co.uk/" />
            <meta property="og:type" content="website" />
            <meta property="og:title" content={TARGET_APP_NAME} />
            <meta property="og:description" content="Transport Anything, Anywhere in the UK. From parcels to machinery, get instant transport quotes, live tracking and dedicated delivery in seconds." />
          </>
        )}
        <link rel="icon" type="image/x-icon" href="/favicon.ico" />
        <link rel="icon" type="image/png" sizes="32x32" href="/favicon-32x32.png" />
        <link rel="icon" type="image/png" sizes="16x16" href="/favicon-16x16.png" />
        <link rel="apple-touch-icon" sizes="180x180" href="/apple-touch-icon.png" />
      </Helmet>
      <div className="min-h-screen bg-slate-50">
        <Toaster position="top-right" richColors />
        <MobileShellProvider>
          <BrowserRouter>
            {shouldUseMobileShell() && <MobileSplash />}
            {shouldUseMobileShell() && <MobileTopBar />}
            {IS_DRIVER_APP && <DriverAppRoutes />}
            {IS_CUSTOMER_APP && <CustomerAppRoutes />}
            {IS_WEB && <WebAppRoutes />}
            {shouldUseMobileShell() && IS_CUSTOMER_APP && <CustomerBottomNav />}
            {shouldUseMobileShell() && IS_WEB &&
              !/^\/driver(\/|$)/.test(window.location.pathname) && (
                /* Preview-only: when ?mobile=1 is set on the WEB build, render
                   the customer-style bottom nav so designers can QA the shell.
                   Suppress on /driver/* paths — driver dashboard owns its own
                   t247-bottomnav. */
                <CustomerBottomNav />
              )}
            <PWAInstallPrompt />
          </BrowserRouter>
        </MobileShellProvider>
      </div>
    </HelmetProvider>
  );
}

export default App;
